import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowUpRight, BookOpen, Search } from "lucide-react";
import { format } from "date-fns";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { BLOG_POSTS } from "@/data/blog-posts";
import { supabase } from "@/integrations/supabase/client";
import { seoCanonicalLink, seoOgUrl } from "@/lib/seo";

export const Route = createFileRoute("/articles")({
  component: BlogPage,
  head: () => ({
    meta: [
      { title: "SimpleTech Insights Blog — IT, Cybersecurity & Web Tips" },
      {
        name: "description",
        content:
          "Expert tips, industry news, and insights into the world of IT, cybersecurity, and small business technology.",
      },
      { property: "og:title", content: "SimpleTech Insights Blog" },
      {
        property: "og:description",
        content: "Expert tips, industry news, and insights into the world of IT and security.",
      },
      seoOgUrl("/articles"),
    ],
    links: [seoCanonicalLink("/articles")],
  }),
});

const FILTERS = ["All", "Technology", "Security", "Cyber Security", "AI", "ADA Website Compliance", "Software Integration", "Web Design", "Managed IT", "VoIP", "Senior Tech Support", "Tech Support"] as const;
type Filter = (typeof FILTERS)[number];

type UnifiedPost = {
  slug: string;
  title: string;
  excerpt: string;
  author: string;
  date: string;
  isoDate: string;
  category: Filter;
  coverImage?: string;
  source: "cms" | "static";
};

function BlogPage() {
  const [filter, setFilter] = useState<Filter>("All");
  const [search, setSearch] = useState("");

  const queryClient = useQueryClient();
  const { data: cmsPosts } = useQuery({
    queryKey: ["public", "posts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("posts")
        .select("id, title, slug, excerpt, featured_image, published_at, categories(name)")
        .eq("status", "published")
        .lte("published_at", new Date().toISOString())
        .order("published_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    const channel = supabase
      .channel("public-posts-articles-changes")
      .on("postgres_changes", { event: "*", schema: "public", table: "posts" }, () => {
        queryClient.invalidateQueries({ queryKey: ["public", "posts"] });
      })
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  const cmsUnified: UnifiedPost[] = (cmsPosts ?? []).map((p) => {
    const catName = p.categories?.name ?? "Technology";
    const category: Filter = (FILTERS as readonly string[]).includes(catName)
      ? (catName as Filter)
      : "Technology";
    return {
      slug: p.slug,
      title: p.title,
      excerpt: p.excerpt ?? "",
      author: "SimpleTech",
      date: p.published_at ? format(new Date(p.published_at), "PP") : "",
      isoDate: p.published_at ?? new Date().toISOString(),
      category,
      coverImage: p.featured_image ?? undefined,
      source: "cms",
    };
  });

  const staticUnified: UnifiedPost[] = BLOG_POSTS.map((p) => ({
    slug: p.slug,
    title: p.title,
    excerpt: p.excerpt,
    author: p.author,
    date: p.date,
    isoDate: p.isoDate,
    category: p.category,
    coverImage: p.coverImage,
    source: "static",
  }));

  const allPosts = [...cmsUnified, ...staticUnified].sort((a, b) =>
    b.isoDate.localeCompare(a.isoDate),
  );

  const filteredPosts = allPosts.filter((post) => {
    const matchesCategory = filter === "All" || post.category === filter;
    const matchesSearch =
      !search.trim() ||
      [post.title, post.excerpt, post.category, post.author].some((field) =>
        field.toLowerCase().includes(search.trim().toLowerCase()),
      );
    return matchesCategory && matchesSearch;
  });

  const featured = filteredPosts.find((p) => !!p.coverImage);
  const rest = filteredPosts;

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <SiteHeader active="blog" />
      <main id="main-content" className="flex-1">
        <section className="bg-white pt-16 pb-12 md:pt-24 md:pb-16">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-sky-50 text-sky-500 border border-sky-100 text-xs font-bold tracking-wider uppercase">
              <BookOpen className="w-3.5 h-3.5" /> Blog
            </div>
            <h1 className="mt-6 text-4xl md:text-6xl font-extrabold text-[#0b0a3d] tracking-tight">
              SimpleTech Insights Blog
            </h1>
            <p className="mt-5 text-slate-600 text-lg leading-relaxed max-w-2xl mx-auto">
              Expert tips, industry news, and insights into the world of IT and security.
            </p>
          </div>
        </section>

        {featured && (
          <section className="bg-white pb-16">
            <div className="max-w-6xl mx-auto px-6">
              <div className="rounded-3xl bg-sky-400 overflow-hidden shadow-xl">
                <div className="grid md:grid-cols-2 gap-0">
                  <div className="bg-white p-6 md:p-8 flex items-center justify-center">
                    {featured.coverImage ? (
                      <img
                        src={featured.coverImage}
                        alt={featured.title}
                        className="w-full h-full max-h-96 object-cover rounded-2xl"
                      />
                    ) : (
                      <div className="w-full h-72 rounded-2xl bg-gradient-to-br from-sky-100 to-sky-50" />
                    )}
                  </div>
                  <div className="p-8 md:p-12 text-white flex flex-col justify-center">
                    <p className="text-sm font-semibold text-sky-100">
                      {featured.author} · {featured.date}
                    </p>
                    <h2 className="mt-3 text-3xl md:text-4xl font-extrabold leading-tight">
                      {featured.title}
                    </h2>
                    <p className="mt-4 text-white/90 leading-relaxed">{featured.excerpt}</p>
                    <div>
                      <Link
                        to="/articles/$slug"
                        params={{ slug: featured.slug }}
                        className="mt-6 inline-block bg-white text-sky-500 font-bold text-sm px-6 py-3 rounded-md hover:bg-sky-50 transition-colors"
                      >
                        Read More
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        <section className="bg-white pb-20 md:pb-28">
          <div className="max-w-6xl mx-auto px-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-10">
              <h2 className="text-3xl md:text-4xl font-extrabold text-[#0b0a3d] tracking-tight">
                Latest Blog Posts
              </h2>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search articles..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9 pr-4 py-2 rounded-full bg-white border border-slate-200 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-300 focus:border-transparent w-full sm:w-56"
                  />
                </div>
                <div className="inline-flex items-center gap-1 p-1 rounded-full bg-slate-100">
                  {FILTERS.map((f) => (
                    <button
                      key={f}
                      onClick={() => setFilter(f)}
                      className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                        filter === f
                          ? "bg-white text-[#0b0a3d] shadow-sm"
                          : "text-slate-500 hover:text-[#0b0a3d]"
                      }`}
                    >
                      {f}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {filteredPosts.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-slate-500 text-lg">No posts found matching "{search}"</p>
                <button
                  onClick={() => {
                    setSearch("");
                    setFilter("All");
                  }}
                  className="mt-4 text-sky-500 font-semibold hover:underline"
                >
                  Clear search
                </button>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-10">
                {rest.map((post) => (
                  <Link
                    key={`${post.source}-${post.slug}`}
                    to="/articles/$slug"
                    params={{ slug: post.slug }}
                    className="group block"
                  >
                    <p className="text-xs text-slate-500 font-medium">• {post.date}</p>
                    <div className="mt-2 flex items-start justify-between gap-3">
                      <h3 className="text-lg font-bold text-[#0b0a3d] leading-snug group-hover:text-sky-500 transition-colors">
                        {post.title}
                      </h3>
                      <ArrowUpRight className="w-5 h-5 text-slate-400 shrink-0 group-hover:text-sky-500 transition-colors" />
                    </div>
                    <p className="mt-3 text-sm text-slate-500 leading-relaxed line-clamp-2">
                      {post.excerpt}
                    </p>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="bg-white pb-20">
          <div className="max-w-6xl mx-auto px-6">
            <div className="rounded-3xl bg-sky-50 border border-sky-100 px-6 py-14 md:py-20 text-center">
              <h2 className="text-3xl md:text-4xl font-extrabold text-[#0b0a3d] tracking-tight">
                Ready to Transform
                <br />
                Your IT Infrastructure?
              </h2>
              <p className="mt-5 text-slate-600 max-w-xl mx-auto">
                Schedule a free strategy call today and see the difference SimpleTech can make for
                your business.
              </p>
              <Link
                to="/contact"
                className="mt-8 inline-block bg-sky-600 hover:bg-sky-700 text-white font-bold text-sm tracking-wider px-7 py-3 rounded-md transition-colors"
              >
                Get Started
              </Link>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
